import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

function PublicGroupsMap() {
  const [groups, setGroups] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/api/public/groups')
      .then(res => {
        setGroups(res.data);
        setFiltered(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = groups;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(g =>
        g.group_name?.toLowerCase().includes(s) ||
        g.city?.toLowerCase().includes(s) ||
        g.state?.toLowerCase().includes(s)
      );
    }
    if (stateFilter) result = result.filter(g => g.state === stateFilter);
    setFiltered(result);
  }, [search, stateFilter, groups]);

  const states = [...new Set(groups.map(g => g.state))].filter(Boolean).sort();

  if (loading) return <div className="loading">Loading groups...</div>;

  if (selected) {
    return (
      <div className="map-page">
        <button
          onClick={() => setSelected(null)}
          style={{ marginBottom: '1rem', padding: '0.5rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif' }}
        >
          ← Back to Groups
        </button>
        <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', borderRadius: '4px', padding: '2rem' }}>
          <h1 style={{ marginTop: 0 }}>{selected.group_name}</h1>
          <p style={{ color: 'var(--text-muted)' }}>📍 {selected.city}, {selected.state}</p>
          {selected.year_established && <p><strong>Year Established:</strong> {selected.year_established}</p>}

          {selected.photos && selected.photos.length > 0 && (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {selected.photos.filter(Boolean).map((photo, i) => (
                <img key={i} src={`${UPLOADS_URL}/${photo}`} alt={`${selected.group_name} photo ${i + 1}`}
                  style={{ maxWidth: '400px', maxHeight: '400px', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border)' }}
                />
              ))}
            </div>
          )}

          <h3>About</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{selected.group_bio}</p>

          {selected.event_types && <><h3>Events</h3><p>{selected.event_types}</p></>}
          {selected.resources && <><h3>Resources</h3><p>{selected.resources}</p></>}

          <h3>Membership</h3>
          <p><strong>Accepts New Members:</strong> {selected.accepts_new_members}</p>
          {selected.membership_criteria && <p>{selected.membership_criteria}</p>}

          <h3>Contact & Social</h3>
          {selected.publish_contact_name && selected.primary_contact_name && <p><strong>Contact:</strong> {selected.primary_contact_name}</p>}
          {selected.publish_contact_email && selected.primary_contact_email && <p><strong>Email:</strong> {selected.primary_contact_email}</p>}
          {selected.website && <p><strong>Website:</strong> <a href={selected.website.startsWith('http') ? selected.website : 'https://' + selected.website} target="_blank" rel="noopener noreferrer">{selected.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a></p>}
          {selected.facebook && <p><strong>Facebook:</strong> <a href={selected.facebook.startsWith('http') ? selected.facebook : 'https://' + selected.facebook} target="_blank" rel="noopener noreferrer">{selected.facebook.replace(/^https?:\/\/(www\.)?facebook\.com\//, '@').replace(/\/$/, '')}</a></p>}
          {selected.instagram && <p><strong>Instagram:</strong> <a href={selected.instagram.startsWith('http') ? selected.instagram : 'https://instagram.com/' + selected.instagram.replace(/^@/, '')} target="_blank" rel="noopener noreferrer">{('@' + selected.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/^@/, '').replace(/\/$/, ''))}</a></p>}
          {selected.tiktok && <p><strong>TikTok:</strong> <a href={selected.tiktok.startsWith('http') ? selected.tiktok : 'https://tiktok.com/@' + selected.tiktok.replace(/^@/, '')} target="_blank" rel="noopener noreferrer">{('@' + selected.tiktok.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, ''))}</a></p>}
          {selected.youtube && <p><strong>YouTube:</strong> <a href={selected.youtube.startsWith('http') ? selected.youtube : 'https://' + selected.youtube} target="_blank" rel="noopener noreferrer">{selected.youtube.replace(/^https?:\/\/(www\.)?youtube\.com\//, '').replace(/\/$/, '')}</a></p>}
        </div>
      </div>
    );
  }

  return (
    <div className="map-page">
      <h1>Krampus Groups & Passes</h1>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, city, or state..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="">All States/Provinces</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={() => { setSearch(''); setStateFilter(''); }}
          style={{ padding: '0.6rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontSize: '0.85rem' }}
        >
          Clear
        </button>
      </div>

      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        Showing {filtered.length} of {groups.length} groups
      </p>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <p>No groups found matching your search.</p>
        </div>
      ) : (
        <div className="list-grid">
          {filtered.map(group => (
            <div
              key={group.id}
              className="card"
              onClick={() => setSelected(group)}
              style={{ cursor: 'pointer' }}
            >
              {group.photos && group.photos[0] ? (
                <img
                  src={`${UPLOADS_URL}/${group.photos[0]}`}
                  alt={group.group_name}
                  style={{ width: '100%', height: '300px', objectFit: 'contain', background: 'var(--dark-3)' }}
                />
              ) : (
                <div style={{ height: '200px', background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '3rem' }}>
                  ⛧
                </div>
              )}
              <div className="card-body">
                <h3 className="card-title">{group.group_name}</h3>
                <p className="card-location">📍 {group.city}, {group.state}</p>
                {group.year_established && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                    Est. {group.year_established}
                  </p>
                )}
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.5rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                  {group.group_bio}
                </p>
                <button style={{ marginTop: '0.75rem', padding: '0.4rem 0.8rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontSize: '0.8rem' }}>
                  View Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PublicGroupsMap;
